import { RouteRecordRaw } from 'vue-router'
import { MenuItem } from '../stores/user'
import Layout from '../components/Layout.vue'

// 视图组件映射表
const componentMap: Record<string, () => Promise<typeof import('*.vue')>> = {
  OpenAppManagement: () => import('../views/openapp/index.vue'),
  GatewayOrderManagement: () => import('../views/gateway/order/index.vue'),
  'monitor/DeliveryTask': () => import('../views/monitor/DeliveryTask.vue'),
  'monitor/ChannelCallLog': () => import('../views/monitor/ChannelCallLog.vue'),
  'monitor/OpenApiRequestLog': () => import('../views/monitor/OpenApiRequestLog.vue')
}

/**
 * 递归过滤异步路由
 * @param menus 后端返回的菜单树
 * @returns Vue Router 路由配置数组
 */
export function filterAsyncRouter(menus: MenuItem[]): RouteRecordRaw[] {
  const routes: RouteRecordRaw[] = []

  // 需要屏蔽的菜单组件列表
  const hiddenComponents: string[] = []

  menus.forEach(menu => {
    // 跳过隐藏菜单
    if (menu.hidden) {
      return
    }

    // 跳过屏蔽菜单
    if (menu.component && hiddenComponents.includes(menu.component)) {
      return
    }

    // 如果没有 component 但有子菜单，这是一个父级菜单，使用 Layout 作为容器
    // 如果没有 component 也没有子菜单，跳过这个菜单
    if (!menu.component) {
      if (menu.children && menu.children.length > 0) {
        // 父级菜单：使用 Layout 作为布局容器，子菜单作为子路由
        // 过滤掉需要隐藏的子菜单
        const filteredChildren = menu.children.filter(child =>
          !child.component || !hiddenComponents.includes(child.component)
        )
        const childRoutes = filterAsyncRouter(filteredChildren)
        if (childRoutes.length > 0) {
          routes.push({
            path: menu.path,
            name: menu.name,
            component: Layout,
            meta: {
              title: menu.name,
              icon: menu.icon,
              permissions: menu.permissions || [],
              requiresAuth: true
            },
            children: childRoutes
          })
        }
      }
      return
    }

    // 有 component 的菜单
    const route: RouteRecordRaw & { children?: RouteRecordRaw[] } = {
      path: menu.path,
      name: menu.name,
      component: componentMap[menu.component] as () => Promise<typeof import('*.vue')>,
      meta: {
        title: menu.name,
        icon: menu.icon,
        permissions: menu.permissions || [],
        requiresAuth: true
      }
    }

    // 递归处理子路由
    if (menu.children && menu.children.length > 0) {
      const childRoutes = filterAsyncRouter(menu.children)
      if (childRoutes.length > 0) {
        ;(route as RouteRecordRaw & { children?: RouteRecordRaw[] }).children = childRoutes
      }
    }

    routes.push(route)
  })

  return routes
}

/**
 * 将后端菜单转换为侧边栏菜单格式
 * @param menus 后端返回的菜单树
 * @returns 侧边栏菜单格式
 */
export function generateSidebarMenus(menus: MenuItem[]) {
  return menus
    .filter(menu => !menu.hidden)
    .map(menu => {
      const item: any = {
        id: menu.id,
        name: menu.name,
        path: menu.path,
        icon: menu.icon,
        permissions: menu.permissions
      }

      if (menu.children && menu.children.length > 0) {
        item.children = generateSidebarMenus(menu.children)
      }

      return item
    })
}

/**
 * 扁平化权限列表
 * @param menus 菜单树
 * @returns 权限字符串数组
 */
export function flattenPermissions(menus: MenuItem[]): string[] {
  const permissions: string[] = []

  function traverse(menuList: MenuItem[]) {
    menuList.forEach(menu => {
      if (menu.permissions && menu.permissions.length > 0) {
        permissions.push(...menu.permissions)
      }
      if (menu.children && menu.children.length > 0) {
        traverse(menu.children)
      }
    })
  }

  traverse(menus)
  return [...new Set(permissions)] // 去重
}

/**
 * 检查是否有权限
 * @param permissions 用户权限列表
 * @param permission 需要检查的权限
 * @returns boolean
 */
export function hasPermission(permissions: string[], permission: string): boolean {
  // 超级管理员拥有所有权限
  if (permissions.includes('*:*:*')) {
    return true
  }

  // 直接匹配
  if (permissions.includes(permission)) {
    return true
  }

  // 通配符匹配 (如 system:user:* 匹配 system:user:create)
  const parts = permission.split(':')
  for (let i = parts.length - 1; i > 0; i--) {
    const wildcard = [...parts.slice(0, i), '*'].join(':')
    if (permissions.includes(wildcard)) {
      return true
    }
  }

  return false
}
